import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { BaseRespone } from 'app/core/models/common.response';
import { InwardReport, IReportComment, IReportLocking, ReportOpened } from './reports.types';
import { ReportCommentComponent } from './reports/report-comment/report-comment.component';

@Injectable({
    providedIn: 'root',
})
export class ReportsService {
    // Private
    private _report: BehaviorSubject<InwardReport | null> = new BehaviorSubject(
        null
    );
    private _reportComments: BehaviorSubject<IReportComment[] | null> =
        new BehaviorSubject(null);
    private _reportOpened: BehaviorSubject<ReportOpened | null> =
        new BehaviorSubject(null);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) { }

    /**
     * Getter for note
     */
    get report$(): Observable<InwardReport> {
        return this._report.asObservable();
    }

    get reportComments$(): Observable<IReportComment[]> {
        return this._reportComments.asObservable();
    }

    /**
     * Create inward
     */
    createReport(inward: InwardReport): Observable<BaseRespone<InwardReport>> {
        return this._httpClient
            .post<BaseRespone<InwardReport>>(
                `${environment.baseUrl}/Report`,
                inward
            )
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((newProduct) => {
                    // Return the new product
                    if (newProduct.isSuccess) {
                        this._report.next(newProduct.result);
                    }
                    return newProduct;
                })
            );
    }

    /**
     * Get board
     *
     * @param id
     */
    getReport(id: number): Observable<InwardReport> {
        if (id > 0) {
            return this._httpClient
                .get(`${environment.baseUrl}/Report/${id}`)
                .pipe(
                    catchError((error) => {
                        return of(error.error);
                    }),
                    map((response) => response),
                    tap((board) => this._report.next(board.result))
                );
        } else {
            this._report.next({} as InwardReport);
            return of();
        }
    }

    getReportComments(reportId): Observable<BaseRespone<IReportComment[]>> {
        return this._httpClient
            .get<BaseRespone<IReportComment[]>>(
                `${environment.baseUrl}/report/comment/${reportId}`
            )
            .pipe(
                tap((response) => {
                    this._reportComments.next(response.result);
                })
            );
    }

    /**
     * Update the card
     *
     * @param id
     * @param card
     */

    updateReport(inward: InwardReport): Observable<BaseRespone<InwardReport>> {
        return this._httpClient
            .put<BaseRespone<InwardReport>>(
                `${environment.baseUrl}/Report`,
                inward
            )
            .pipe(
                map((response) => {
                    this._report.next(response.result);
                    return response;
                }),
                catchError((error) => {
                    return of(error.error);
                })
            );
    }

    updateFinalizeReport(finalizeReport: any): Observable<BaseRespone<string>> {
        return this._httpClient
            .put<BaseRespone<string>>(
                `${environment.baseUrl}/Report/UpdateFinalize`,
                finalizeReport
            )
            .pipe(
                map((response) => {
                    return response;
                }),
                catchError((error) => {
                    return of(error.error);
                })
            );
    }

    updateReadyForOutwardReport(finalizeReport: any): Observable<BaseRespone<string>> {
        return this._httpClient
            .put<BaseRespone<string>>(
                `${environment.baseUrl}/Report/UpdateReadyForOutward`,
                finalizeReport
            )
            .pipe(
                map((response) => {
                    return response;
                }),
                catchError((error) => {
                    return of(error.error);
                })
            );
    }

    reportOpened(data: ReportOpened): Observable<BaseRespone<ReportOpened>> {
        return this._httpClient
            .post<BaseRespone<ReportOpened>>(
                `${environment.baseUrl}/Report/ReportOpened`,
                data
            )
            .pipe(
                map((response) => {
                    return response;
                })
            );
    }

    createReportComment(
        reportComment: IReportComment
    ): Observable<BaseRespone<IReportComment>> {
        return this.reportComments$.pipe(
            take(1),
            switchMap((reportComments) =>
                this._httpClient
                    .post<BaseRespone<InwardReport>>(
                        `${environment.baseUrl}/Report/Comment`,
                        reportComment
                    )
                    .pipe(
                        catchError((error) => {
                            return of(error.error);
                        }),
                        map((newProduct) => {
                            // Return the new product
                            if (newProduct.isSuccess) {
                                if (newProduct.isSuccess) {
                                    this._reportComments.next([
                                        newProduct.result,
                                        ...reportComments,
                                    ]);
                                }
                            }
                            return newProduct;
                        })
                    )
            )
        );
    }

    sendReportId(id: string): Observable<BaseRespone<string>> {
        return this._httpClient
            .post<BaseRespone<string>>(`${environment.baseUrl}/Report/SendId`, {
                id,
            })
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((response) => {
                    // Handle the response
                    if (response.isSuccess) {
                        //console.log('ID sent successfully:', response.result);
                    }
                    return response;
                })
            );
    }

    reportLocking(lockData: IReportLocking): Observable<BaseRespone<string>> {
        return this._httpClient
            .post<BaseRespone<string>>(`${environment.baseUrl}/ReportLock/Acquire`, lockData)
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((response) => {
                    // Handle the response
                    if (response.isSuccess) {
                        //console.log('ID sent successfully:', response.result);
                    }
                    return response;
                })
            );
    }

    reportUnlocking(lockData: IReportLocking): Observable<BaseRespone<string>> {
        return this._httpClient
            .post<BaseRespone<string>>(`${environment.baseUrl}/ReportLock/Release`, lockData)
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((response) => {
                    // Handle the response
                    if (response.isSuccess) {
                        //console.log('ID sent successfully:', response.result);
                    }
                    return response;
                })
            );
    }

    reportRefresh(lockData: IReportLocking): Observable<BaseRespone<string>> {
        return this._httpClient
            .post<BaseRespone<string>>(`${environment.baseUrl}/ReportLock/Refresh`, lockData)
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((response) => {
                    // Handle the response
                    if (response.isSuccess) {
                        //console.log('ID sent successfully:', response.result);
                    }
                    return response;
                })
            );
    }
}
